import { Slot } from "@radix-ui/react-slot";
import { type VariantProps, cva } from "class-variance-authority";
import * as React from "react";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
	"cursor-pointer inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-xl text-sm font-medium transition-all duration-300 ease-out focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 active:scale-[0.98]",
	{
		variants: {
			variant: {
				default:
					"bg-primary text-primary-foreground shadow-[0_2px_12px_rgba(139,92,246,0.15)] hover:bg-primary/90 hover:shadow-[0_4px_16px_rgba(139,92,246,0.2)]",
				destructive:
					"bg-destructive text-destructive-foreground shadow-[0_2px_10px_rgba(239,68,68,0.12)] hover:bg-destructive/90 hover:shadow-[0_3px_14px_rgba(239,68,68,0.18)]",
				outline:
					"border border-input bg-background-secondary/50 backdrop-blur-sm shadow-[0_1px_8px_rgba(0,0,0,0.06)] hover:bg-accent hover:text-accent-foreground hover:shadow-[0_2px_12px_rgba(0,0,0,0.08)]",
				outlinePrimary:
					"border border-primary/30 text-primary bg-primary/5 backdrop-blur-sm shadow-[0_1px_8px_rgba(139,92,246,0.08)] hover:bg-primary/10 hover:border-primary/50 hover:shadow-[0_2px_12px_rgba(139,92,246,0.12)]",
				outlineSecondary:
					"border border-input/50 bg-card/50 backdrop-blur-sm shadow-[0_1px_6px_rgba(0,0,0,0.04)] hover:bg-accent/50 hover:text-accent-foreground hover:shadow-[0_2px_10px_rgba(0,0,0,0.06)]",
				secondary:
					"bg-white/10 text-white backdrop-blur-sm shadow-[0_2px_12px_rgba(255,255,255,0.06)] hover:bg-white/15 hover:shadow-[0_3px_16px_rgba(255,255,255,0.08)]",
				ghost: "hover:bg-white/5 hover:text-accent-foreground backdrop-blur-sm",
				ghostText: "hover:text-foreground/80",
				link: "text-primary underline-offset-4 hover:underline",
				plain: "bg-transparent text-primary hover:text-primary/80",
			},
			size: {
				default: "h-9 px-4 py-2",
				sm: "h-8 rounded-lg px-3 text-xs",
				lg: "h-10 rounded-xl px-8",
				xl: "h-12 rounded-xl px-12 text-lg",
				icon: "h-9 w-9",
				iconLg: "h-10 w-10 [&_svg]:size-6",
				iconXl: "h-16 w-16 [&_svg]:size-7",
			},
		},
		defaultVariants: {
			variant: "default",
			size: "default",
		},
	},
);

export interface ButtonProps
	extends React.ButtonHTMLAttributes<HTMLButtonElement>,
		VariantProps<typeof buttonVariants> {
	asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
	({ className, variant, size, asChild = false, ...props }, ref) => {
		const Comp = asChild ? Slot : "button";
		return (
			<Comp
				className={cn(buttonVariants({ variant, size, className }))}
				ref={ref}
				{...props}
			/>
		);
	},
);
Button.displayName = "Button";

export { Button, buttonVariants };
